import { createContext, useContext } from "react";

import type { ProBuyerPrice } from "@/features/pro-buyer-price/data/pro-buyer-price-client";
import type { PromotionPresentation } from "@/features/promotions/types";
import type { Product } from "@/features/search/api/types";

/**
 * The fields the card needs, and nothing more.
 *
 * This is now the only product card on the site, so the pages that still get their data from the
 * gateway have to build one of these (see `toCardProduct`). Asking them for a whole search
 * `Product` would mean making up a `merchantId` and a `created` date the card never shows, so the
 * card asks only for the fields it reads. A real search result already has all of them.
 */
export type ProductCardProduct = Pick<
  Product,
  | "id"
  | "slug"
  | "title"
  | "currency"
  | "originalPrice"
  | "buyNowPrice"
  | "discountPercentage"
  | "bidFrom"
  | "sold"
  | "soldAtPrice"
  | "numberOfItems"
  | "images"
  | "favoriteId"
  | "labels"
> &
  /**
   * The card's prices before display conversion, in the listing's own currency (EUR), shown small
   * under the converted price (WHO-5991 decision 7). Null or absent when nothing was converted.
   * The search service sends it as is; gateway cards build it from each price's `unconverted`.
   */
  Partial<Pick<Product, "listingPrices">> & {
    /**
     * WHO-5947. End of a live reservation, unix seconds, when the search
     * service passes the Typesense `on_hold_until` field through. Optional:
     * absent means not reserved. Both spellings are read.
     */
    promotion?: PromotionPresentation | null;
    onHoldUntil?: number | null;
    on_hold_until?: number | null;
  };

export type ProductCardContextValue = {
  /** The product, used as-is — prices are already resolved by the time they get here. */
  product: ProductCardProduct;
  /** Format an amount in this product's currency and the active locale. */
  formatAmount: (amount: number | null | undefined) => string | null;
  /** Format an amount of `listingPrices`, in the listing's own currency (the small euro). */
  formatListingAmount: (amount: number | null | undefined) => string | null;
  /** PDP path, e.g. `/products/<slug>`. */
  href: string;
  /**
   * WHO-5743. The viewer's Pro Buyer price, read client-side after mount, so
   * it is never in SSR HTML. Null for everyone who does not get one.
   */
  proBuyerPrice?: ProBuyerPrice | null;
};

const ProductCardContext = createContext<ProductCardContextValue | null>(null);

export const ProductCardContextProvider = ProductCardContext.Provider;

export const useProductCard = (): ProductCardContextValue => {
  const value = useContext(ProductCardContext);

  if (!value) {
    throw new Error("ProductCard.* must be used within <ProductCard>");
  }

  return value;
};
